import { useEffect, useRef, type ButtonHTMLAttributes } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { gsap, reducedMotion } from './core';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  DOWNLOAD_URL,
  closeDownloadModal,
  handleDownloadIntent,
  useDownloadModalOpen,
} from '../../lib/download';

/** Any "download the app" CTA — deep-links + store-falls-back on a phone
 *  browser, opens the glass modal on desktop. Renders as a real <button>
 *  (not a route Link) but takes `.cine-btn` classes identically. */
export function DownloadCTA({
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" onClick={handleDownloadIntent} {...rest}>
      {children}
    </button>
  );
}

const BENEFITS = [
  'AI plans your itinerary in seconds',
  'Offline maps — no signal required',
  'Split expenses with your travel group',
  'Every memory, synced automatically',
];

function AppleMark() {
  return (
    <svg width="19" height="22" viewBox="0 0 19 22" fill="currentColor" aria-hidden>
      <path d="M15.6 11.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.3 2.6 1.3-.1 1.8-.9 3.4-.9s2 .9 3.4.8c1.4 0 2.3-1.3 3.1-2.5.7-1 1.3-2.1 1.7-3.3-2.2-.9-2.3-3.2-2.3-3.5ZM13.1 3.6c.7-.8 1.1-1.9 1-3-1 .1-2.1.7-2.8 1.5-.6.7-1.1 1.8-1 2.9 1.1.1 2.1-.5 2.8-1.4Z" />
    </svg>
  );
}

function PlayMark() {
  return (
    <svg width="19" height="21" viewBox="0 0 19 21" fill="none" aria-hidden>
      <path d="M1 1.4C.6 1.8.4 2.4.4 3.1v14.8c0 .7.2 1.3.6 1.7l.1.1L9.6 11 1 2.4 1 1.4Z" fill="#00D6C9" />
      <path d="M12.4 13.8 9.6 11l-8.5 8.5c.4.3 1 .4 1.6 0l9.7-5.7Z" fill="#FF6C9F" />
      <path d="M12.4 8.2 9.6 11l2.8 2.8 3.3-1.9c.9-.5.9-1.4 0-1.9l-3.3-1.8Z" fill="#FFB35C" />
      <path d="M1.1.5C.6.9.4 1.5.4 2.2v.9L9.6 11 12.4 8.2 2.7.1c-.5-.3-1.1-.2-1.6.4Z" fill="#37E3C8" />
    </svg>
  );
}

export default function DownloadModal() {
  const open = useDownloadModalOpen();
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    lastFocused.current = document.activeElement;
    const panel = panelRef.current;
    const focusable = panel?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled])'
    );
    focusable?.[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDownloadModal();
        return;
      }
      if (e.key !== 'Tab' || !focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    if (!reducedMotion()) {
      gsap.fromTo(
        panel,
        { autoAlpha: 0, y: 24, scale: 0.96 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.45, ease: 'power3.out' }
      );
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      (lastFocused.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="cine-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeDownloadModal();
      }}
    >
      <div
        className="cine-modal glass"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="download-modal-title"
      >
        <button
          type="button"
          className="cine-modal-close"
          onClick={closeDownloadModal}
          aria-label="Close"
        >
          ✕
        </button>

        <span className="cine-kicker">Rovera · Mobile app</span>
        <h2 id="download-modal-title" className="cine-modal-title">
          Rovera is designed for mobile.
        </h2>
        <p className="cine-modal-lead">
          Download the app to start planning your journeys — AI itineraries, offline maps,
          and shared trips, all in your pocket.
        </p>

        <ul className="cine-modal-benefits">
          {BENEFITS.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>

        <div className="cine-modal-stores">
          <a
            href={APP_STORE_URL}
            className="cine-store-btn"
            target="_blank"
            rel="noreferrer"
          >
            <AppleMark />
            <span>
              <small>Download on the</small>
              App Store
            </span>
          </a>
          <a
            href={PLAY_STORE_URL}
            className="cine-store-btn"
            target="_blank"
            rel="noreferrer"
          >
            <PlayMark />
            <span>
              <small>Get it on</small>
              Google Play
            </span>
          </a>
        </div>

        <div className="cine-modal-qr">
          <div className="cine-modal-qr-code">
            <QRCodeSVG
              value={DOWNLOAD_URL}
              size={92}
              bgColor="transparent"
              fgColor="#eaf0ff"
              level="M"
              marginSize={0}
              title="Scan to download the Rovera app"
            />
          </div>
          <p>
            Scan with your phone's camera —<br />
            we'll take you to the right store automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
